// AGC: Subscription Types for DroidFarm Freemium System
// AGC: Implements three-tier subscription model: FREE, PRO, PREMIUM
// AGC: Based on doc/junior/DROID_FARM_FREEMIUM_PRICING_MODEL.md

/**
 * AGC: Subscription tier levels
 * AGC: - free: "Produtor Iniciante" - R$ 0/month
 * AGC: - pro: "Produtor Profissional" - R$ 99/month
 * AGC: - premium: "Produtor Enterprise" - R$ 299/month
 */
export type SubscriptionTier = 'free' | 'pro' | 'premium'

/**
 * AGC: Billing cycle options
 */
export type BillingCycle = 'monthly' | 'annual'

/**
 * AGC: Subscription status
 */
export type SubscriptionStatus = 'active' | 'trial' | 'cancelled' | 'past_due' | 'pending'

/**
 * AGC: Tier limits configuration
 * AGC: null = unlimited
 */
export interface TierLimits {
  // AGC: Core limits
  maxHectares: number | null
  maxFields: number | null
  maxTasksPerMonth: number | null
  maxVoiceCommandsPerMonth: number | null
  maxWhatsAppAudioPerMonth: number | null
  maxTeamMembers: number | null
  hectareCalculatorUsesPerMonth: number | null
  
  // AGC: Feature flags
  hasOfflineVoice: boolean
  hasWhatsAppIntegration: boolean
  hasAdvancedWeather: boolean        // AGC: 7-day (Pro) or 30-day (Premium) forecast
  hasYieldPotential: boolean         // AGC: Regional averages (Pro) or real-time (Premium)
  hasDroidGrid: boolean              // AGC: Basic soil sampling grid
  hasSmartDroidGrid: boolean         // AGC: AI-optimized sampling (Premium only)
  hasHumanAdvisor: boolean           // AGC: Marketplace access (Premium only)
  hasMultiFarm: boolean              // AGC: Multi-farm management (Premium only)
  hasAdvancedAnalytics: boolean      // AGC: P&L by field, cost tracking (Premium only)
  hasAPIAccess: boolean              // AGC: API integrations (Premium only)
  hasFullEMBRAPA: boolean            // AGC: Full EMBRAPA + ZARC risk levels
  hasEconomicsIntegration: boolean   // AGC: Profitability projections (Premium only)
  hasPrioritySupport: boolean        // AGC: Dedicated support (Premium only)
  hasExportCapabilities: boolean     // AGC: PDF, CSV export (Pro+)
  hasSatelliteImagery: boolean       // AGC: Satellite overlay on fields (Pro+)
  hasPrescriptionMaps: boolean       // AGC: Full prescription map support (Pro+)
  
  // AGC: Satellite Imagery Limits (per month)
  maxSentinelImagesPerMonth: number | null  // AGC: Sentinel-2 NDVI images (10m resolution)
  hasPlanetScope: boolean            // AGC: PlanetScope imagery access (3m resolution) - PRO+
  hasAirbusPleiades: boolean         // AGC: Airbus Pleiades imagery access (50cm resolution) - Premium only
}

/**
 * AGC: Tier configuration from Firestore (full config including limits, features, pricing)
 */
export interface TierConfig {
  name: string
  displayName: string
  description: string
  price: { monthly: number; annual: number; currency: string }
  limits: TierLimits
  features: Record<string, boolean | string>
  voiceSettings: Record<string, boolean | number | string>
  color: string
  icon: string
  badge: string
  order: number
  recommended?: boolean
  createdAt?: string
  updatedAt?: string
}

/**
 * AGC: Default tier configs — fallback when Firestore is unavailable
 * AGC: These are NOT used at runtime when Firestore is healthy.
 * AGC: The admin panel edits are the source of truth via Firestore tierConfig/{tier}.
 */
export const DEFAULT_TIER_CONFIGS: Record<SubscriptionTier, TierConfig> = {
  free: {
    name: 'FREE',
    displayName: 'Produtor Iniciante',
    description: 'Ideal para pequenos produtores que estão começando',
    price: { monthly: 0, annual: 0, currency: 'BRL' },
    limits: {
      maxHectares: 50,
      maxFields: 3,
      maxTasksPerMonth: 30,
      maxVoiceCommandsPerMonth: 50,
      maxWhatsAppAudioPerMonth: 0,
      maxTeamMembers: 0,
      hectareCalculatorUsesPerMonth: 3,
      hasOfflineVoice: false,
      hasWhatsAppIntegration: false,
      hasAdvancedWeather: false,
      hasYieldPotential: false,
      hasDroidGrid: false,
      hasSmartDroidGrid: false,
      hasHumanAdvisor: false,
      hasMultiFarm: false,
      hasAdvancedAnalytics: false,
      hasAPIAccess: false,
      hasFullEMBRAPA: false,
      hasEconomicsIntegration: false,
      hasPrioritySupport: false,
      hasExportCapabilities: false,
      hasSatelliteImagery: false,
      hasPrescriptionMaps: false,
      maxSentinelImagesPerMonth: 1,
      hasPlanetScope: false,
      hasAirbusPleiades: false,
    },
    features: { voiceMode: 'B' },
    voiceSettings: { enableGPTFallback: true, enableOfflineQueue: true, autoProcessQueue: true, maxQueueSize: 50, defaultLanguage: 'pt-BR' },
    color: 'emerald',
    icon: 'Gift',
    badge: 'GRÁTIS',
    order: 0,
  },
  pro: {
    name: 'PRO',
    displayName: 'Produtor Profissional',
    description: 'Para produtores em crescimento com mais recursos',
    price: { monthly: 19.90, annual: 199, currency: 'BRL' },
    limits: {
      maxHectares: 500,
      maxFields: 20,
      maxTasksPerMonth: 300,
      maxVoiceCommandsPerMonth: 500,
      maxWhatsAppAudioPerMonth: 100,
      maxTeamMembers: 5,
      hectareCalculatorUsesPerMonth: null,
      hasOfflineVoice: true,
      hasWhatsAppIntegration: true,
      hasAdvancedWeather: true,
      hasYieldPotential: true,
      hasDroidGrid: true,
      hasSmartDroidGrid: false,
      hasHumanAdvisor: false,
      hasMultiFarm: false,
      hasAdvancedAnalytics: false,
      hasAPIAccess: false,
      hasFullEMBRAPA: true,
      hasEconomicsIntegration: false,
      hasPrioritySupport: false,
      hasExportCapabilities: true,
      hasSatelliteImagery: true,
      hasPrescriptionMaps: true,
      maxSentinelImagesPerMonth: 5,
      hasPlanetScope: true,
      hasAirbusPleiades: true,
    },
    features: { voiceMode: 'A' },
    voiceSettings: { enableGPTFallback: true, enableOfflineQueue: true, autoProcessQueue: true, maxQueueSize: 100, defaultLanguage: 'pt-BR' },
    color: 'blue',
    icon: 'Star',
    badge: 'PRO',
    recommended: true,
    order: 1,
  },
  premium: {
    name: 'PREMIUM',
    displayName: 'Produtor Enterprise',
    description: 'Solução completa para grandes operações e multi-fazendas',
    price: { monthly: 39.90, annual: 399, currency: 'BRL' },
    limits: {
      maxHectares: null,
      maxFields: null,
      maxTasksPerMonth: null,
      maxVoiceCommandsPerMonth: null,
      maxWhatsAppAudioPerMonth: null,
      maxTeamMembers: null,
      hectareCalculatorUsesPerMonth: null,
      hasOfflineVoice: true,
      hasWhatsAppIntegration: true,
      hasAdvancedWeather: true,
      hasYieldPotential: true,
      hasDroidGrid: true,
      hasSmartDroidGrid: true,
      hasHumanAdvisor: true,
      hasMultiFarm: true,
      hasAdvancedAnalytics: true,
      hasAPIAccess: true,
      hasFullEMBRAPA: true,
      hasEconomicsIntegration: true,
      hasPrioritySupport: true,
      hasExportCapabilities: true,
      hasSatelliteImagery: true,
      hasPrescriptionMaps: true,
      maxSentinelImagesPerMonth: 10,
      hasPlanetScope: true,
      hasAirbusPleiades: true,
    },
    features: { voiceMode: 'A' },
    voiceSettings: { enableGPTFallback: true, enableOfflineQueue: true, autoProcessQueue: true, maxQueueSize: 200, defaultLanguage: 'pt-BR' },
    color: 'yellow',
    icon: 'Crown',
    badge: 'PREMIUM',
    order: 2,
  },
}

/**
 * AGC: TIER_LIMITS — derived from DEFAULT_TIER_CONFIGS for backward compatibility
 * AGC: Components should migrate to reading from SubscriptionContext.tierConfigs instead
 */
export const TIER_LIMITS: Record<SubscriptionTier, TierLimits> = {
  free: DEFAULT_TIER_CONFIGS.free.limits,
  pro: DEFAULT_TIER_CONFIGS.pro.limits,
  premium: DEFAULT_TIER_CONFIGS.premium.limits,
}

/**
 * AGC: Tier pricing in BRL (Brazilian Real)
 */
export interface TierPricing {
  monthly: number
  annual: number
  annualSavings: number
  perHectareMonth: number | null
}

/**
 * AGC: TIER_PRICING — derived from DEFAULT_TIER_CONFIGS for backward compatibility
 * AGC: Components should migrate to reading from SubscriptionContext.tierConfigs instead
 */
export const TIER_PRICING: Record<SubscriptionTier, TierPricing> = {
  free: {
    monthly: DEFAULT_TIER_CONFIGS.free.price.monthly,
    annual: DEFAULT_TIER_CONFIGS.free.price.annual,
    annualSavings: 0,
    perHectareMonth: 0,
  },
  pro: {
    monthly: DEFAULT_TIER_CONFIGS.pro.price.monthly,
    annual: DEFAULT_TIER_CONFIGS.pro.price.annual,
    annualSavings: 39.80, // AGC: 12 * 19.90 = 238.80 - 199 = 39.80
    perHectareMonth: 0.04, // AGC: At 500 ha
  },
  premium: {
    monthly: DEFAULT_TIER_CONFIGS.premium.price.monthly,
    annual: DEFAULT_TIER_CONFIGS.premium.price.annual,
    annualSavings: 79.80, // AGC: 12 * 39.90 = 478.80 - 399 = 79.80
    perHectareMonth: null,
  },
}

/**
 * AGC: Hectare add-on pricing (for farms exceeding tier limits)
 */
export interface HectareAddonPricing {
  rangeStart: number
  rangeEnd: number | null // AGC: null = unlimited
  proRate: number         // AGC: R$/ha/month for Pro users
  premiumRate: number     // AGC: R$/ha/month for Premium users
}

export const HECTARE_ADDON_PRICING: HectareAddonPricing[] = [
  { rangeStart: 0, rangeEnd: 500, proRate: 0, premiumRate: 0 }, // AGC: Included
  { rangeStart: 501, rangeEnd: 1000, proRate: 0.15, premiumRate: 0 }, // AGC: Included in premium
  { rangeStart: 1001, rangeEnd: 2500, proRate: 0.12, premiumRate: 0.08 },
  { rangeStart: 2501, rangeEnd: 5000, proRate: 0.10, premiumRate: 0.06 },
  { rangeStart: 5001, rangeEnd: null, proRate: 0.08, premiumRate: 0.04 },
]

/**
 * AGC: User subscription record stored in Firestore
 * AGC: Location: /users/{userId}/subscription
 */
export interface UserSubscription {
  tier: SubscriptionTier
  status: SubscriptionStatus
  startDate: string        // AGC: ISO date string
  endDate?: string         // AGC: For cancelled/expiring subscriptions
  billingCycle: BillingCycle
  trialEndDate?: string    // AGC: If on trial
  
  // AGC: Payment integration (for future Stripe integration)
  priceId?: string         // AGC: Stripe price ID
  customerId?: string      // AGC: Stripe customer ID
  subscriptionId?: string  // AGC: Stripe subscription ID
  
  // AGC: Add-on hectares (if exceeding tier limit)
  additionalHectares?: number
  additionalHectaresPrice?: number
  
  // AGC: Metadata
  createdAt: string
  updatedAt: string
  cancelledAt?: string
  cancelReason?: string
}

/**
 * AGC: Usage tracking per month
 * AGC: Location: /users/{userId}/usage/{YYYY-MM}
 */
export interface UsageTracker {
  month: string // AGC: YYYY-MM format
  userId: string
  
  // AGC: Usage counters
  tasksCreated: number
  voiceCommandsUsed: number
  whatsAppAudiosProcessed: number
  hectareCalculatorUses: number
  fieldsCreated: number
  
  // AGC: Team members count (not monthly, but tracked)
  currentTeamMembers: number
  
  // AGC: Total hectares registered
  totalHectares: number
  
  // AGC: Satellite imagery usage counters
  sentinelImagesGenerated: number       // AGC: Sentinel-2 NDVI images generated this month
  planetScopeImagesGenerated: number    // AGC: PlanetScope images generated this month
  airbusImagesGenerated: number         // AGC: Airbus Pleiades images generated this month
  
  // AGC: Metadata
  createdAt: string
  updatedAt: string
}

/**
 * AGC: Default usage tracker for new month
 */
export const DEFAULT_USAGE: Omit<UsageTracker, 'month' | 'userId' | 'createdAt' | 'updatedAt'> = {
  tasksCreated: 0,
  voiceCommandsUsed: 0,
  whatsAppAudiosProcessed: 0,
  hectareCalculatorUses: 0,
  fieldsCreated: 0,
  currentTeamMembers: 1,
  totalHectares: 0,
  // AGC: Satellite imagery counters
  sentinelImagesGenerated: 0,
  planetScopeImagesGenerated: 0,
  airbusImagesGenerated: 0,
}

/**
 * AGC: Default subscription for new users (FREE tier)
 */
export const DEFAULT_SUBSCRIPTION: Omit<UserSubscription, 'createdAt' | 'updatedAt'> = {
  tier: 'free',
  status: 'active',
  startDate: new Date().toISOString(),
  billingCycle: 'monthly',
}

/**
 * AGC: Limit check result
 */
export interface LimitCheckResult {
  allowed: boolean
  current: number
  limit: number | null  // AGC: null = unlimited
  percentage: number    // AGC: 0-100, or 0 if unlimited
  feature: string
  upgradeRequired?: SubscriptionTier // AGC: Tier needed for this feature
}

/**
 * AGC: Feature types that can be limited
 */
export type LimitedFeature = 
  | 'tasks'
  | 'fields'
  | 'voiceCommands'
  | 'whatsAppAudio'
  | 'hectares'
  | 'teamMembers'
  | 'hectareCalculator'
  | 'sentinelImages'        // AGC: Sentinel-2 satellite imagery generation
  | 'planetScopeImages'     // AGC: PlanetScope satellite imagery generation
  | 'airbusImages'          // AGC: Airbus Pleiades satellite imagery generation

/**
 * AGC: Tier display information for UI
 */
export interface TierDisplayInfo {
  tier: SubscriptionTier
  name: string
  tagline: string
  description: string
  color: string
  bgColor: string
  borderColor: string
  icon: string // AGC: Lucide icon name
  badge: string
  recommended?: boolean
}

export const TIER_DISPLAY_INFO: Record<SubscriptionTier, TierDisplayInfo> = {
  free: {
    tier: 'free',
    name: 'Produtor Iniciante',
    tagline: 'Gratuito',
    description: 'Ideal para pequenas propriedades até 50 ha',
    color: 'text-emerald-600',
    bgColor: 'bg-emerald-50 dark:bg-emerald-900/20',
    borderColor: 'border-emerald-500',
    icon: 'Gift',
    badge: 'GRÁTIS',
  },
  pro: {
    tier: 'pro',
    name: 'Produtor Profissional',
    tagline: 'R$ 19,90/mês*',
    description: 'Para propriedades de 50-500 ha em crescimento',
    color: 'text-blue-600',
    bgColor: 'bg-blue-50 dark:bg-blue-900/20',
    borderColor: 'border-blue-500',
    icon: 'Star',
    badge: 'PRO',
    recommended: true,
  },
  premium: {
    tier: 'premium',
    name: 'Produtor Enterprise',
    tagline: 'R$ 39,90/mês*',
    description: 'Para grandes operações e multi-fazendas',
    color: 'text-yellow-600',
    bgColor: 'bg-yellow-50 dark:bg-yellow-900/20',
    borderColor: 'border-yellow-500',
    icon: 'Crown',
    badge: 'PREMIUM',
  },
}

/**
 * AGC: Feature comparison item for tier comparison table
 */
export interface FeatureComparison {
  feature: string
  category: 'limits' | 'mapping' | 'recommendations' | 'weather' | 'voice' | 'hardware' | 'advisor' | 'support'
  free: string | boolean
  pro: string | boolean
  premium: string | boolean
}

/**
 * AGC: Complete feature comparison list
 * AGC: Based on pricing model document section 3
 */
export const FEATURE_COMPARISON: FeatureComparison[] = [
  // AGC: Limits
  { feature: 'Hectares Incluídos', category: 'limits', free: 'Até 50 ha', pro: 'Até 500 ha', premium: 'Ilimitado' },
  { feature: 'Talhões/Lotes', category: 'limits', free: '3', pro: '20', premium: 'Ilimitado' },
  { feature: 'Tarefas/Mês', category: 'limits', free: '30', pro: '300', premium: 'Ilimitado' },
  { feature: 'Comandos de Voz/Mês', category: 'limits', free: '50 (online)', pro: '500 (on+offline)', premium: 'Ilimitado' },
  { feature: 'Áudios WhatsApp/Mês', category: 'limits', free: false, pro: '100', premium: 'Ilimitado' },
  { feature: 'Membros da Equipe', category: 'limits', free: '1', pro: '5', premium: 'Ilimitado' },
  
  // AGC: Mapping & Measurement
  { feature: 'Calculadora de Hectares', category: 'mapping', free: '3 usos/mês', pro: 'Ilimitado', premium: 'Ilimitado + Export' },
  { feature: 'Mapeamento de Talhões', category: 'mapping', free: 'Desenho manual', pro: 'Imagem satélite', premium: 'Multi-fazenda' },
  { feature: 'Droid Grid (Amostragem)', category: 'mapping', free: false, pro: 'Grade básica', premium: 'Grade IA otimizada' },
  { feature: 'Imagens Sentinel-2 (10m)', category: 'mapping', free: '1/mês', pro: '5/mês', premium: '10/mês' },
  { feature: 'PlanetScope (3m)', category: 'mapping', free: false, pro: true, premium: true },
  { feature: 'Airbus Pleiades (50cm)', category: 'mapping', free: false, pro: true, premium: true },
  
  // AGC: Recommendations
  { feature: 'Sugestões EMBRAPA', category: 'recommendations', free: 'Top 3 apenas', pro: 'Ranking completo + ZARC', premium: 'Completo + Economia' },
  { feature: 'Gerador de Plano de Plantio', category: 'recommendations', free: 'Calendário básico', pro: 'Auto-gerado', premium: 'Otimizado por IA' },
  { feature: 'Criação de Tarefas', category: 'recommendations', free: 'Manual', pro: 'Voz + WhatsApp', premium: 'Voz + WhatsApp + IA' },
  
  // AGC: Weather & Yield
  { feature: 'Integração Clima', category: 'weather', free: 'Apenas atual', pro: '7 dias + Alertas', premium: '30 dias + Insights IA' },
  { feature: 'Potencial de Rendimento', category: 'weather', free: false, pro: 'Médias regionais', premium: 'Tempo real + Vizinhos' },
  
  // AGC: Voice & WhatsApp
  { feature: 'Reconhecimento de Voz', category: 'voice', free: 'Apenas online', pro: 'Online + Offline', premium: 'Todos os modos' },
  { feature: 'Integração WhatsApp', category: 'voice', free: false, pro: '100/mês', premium: 'Ilimitado' },
  
  // AGC: Hardware
  { feature: 'Sync FarmPro/Agrologic', category: 'hardware', free: 'Download básico', pro: 'Integração completa', premium: 'Completo + Telemetria' },
  { feature: 'Mapas de Prescrição', category: 'hardware', free: 'Apenas visualizar', pro: 'Upload/Download', premium: 'Criar + Otimizar' },
  { feature: 'Mapas As-Applied', category: 'hardware', free: false, pro: 'Visualizar', premium: 'Analytics' },
  
  // AGC: Human Advisor
  { feature: 'Acesso ao Marketplace', category: 'advisor', free: false, pro: false, premium: true },
  { feature: 'Consultas com Agrônomos', category: 'advisor', free: false, pro: false, premium: 'Pagar por uso' },
  { feature: 'Pacotes de Assinatura', category: 'advisor', free: false, pro: false, premium: 'Disponível' },
  
  // AGC: Support
  { feature: 'Nível de Suporte', category: 'support', free: 'Comunidade', pro: 'Email + Chat', premium: 'Prioritário + Dedicado' },
  { feature: 'Onboarding', category: 'support', free: 'Self-service', pro: 'Vídeo tutoriais', premium: 'Treinamento personalizado' },
]

/**
 * AGC: Upgrade trigger messages
 * AGC: Used to show contextual upgrade prompts
 */
export interface UpgradeTrigger {
  feature: LimitedFeature
  triggerPercentage: number // AGC: Show warning at this % of limit
  message: string
  upgradeMessage: string
  conversionRate?: number // AGC: Expected conversion rate
}

export const UPGRADE_TRIGGERS: UpgradeTrigger[] = [
  {
    feature: 'hectareCalculator',
    triggerPercentage: 100,
    message: 'Você usou suas {current} medições de hectares este mês',
    upgradeMessage: 'Faça upgrade para PRO e tenha medições ilimitadas',
    conversionRate: 0.45,
  },
  {
    feature: 'hectares',
    triggerPercentage: 100,
    message: 'Sua fazenda excede {limit} hectares do plano {tier}',
    upgradeMessage: 'Adicione os hectares restantes com o plano PRO',
    conversionRate: 0.60,
  },
  {
    feature: 'voiceCommands',
    triggerPercentage: 80,
    message: 'Você usou {current} de {limit} comandos de voz este mês',
    upgradeMessage: 'Comandos offline disponíveis no PRO',
    conversionRate: 0.35,
  },
  {
    feature: 'tasks',
    triggerPercentage: 80,
    message: 'Você criou {current} de {limit} tarefas este mês',
    upgradeMessage: 'Aumente seu limite para 300 tarefas/mês',
    conversionRate: 0.30,
  },
  {
    feature: 'fields',
    triggerPercentage: 100,
    message: 'Você atingiu o limite de {limit} talhões',
    upgradeMessage: 'Faça upgrade para adicionar mais talhões',
    conversionRate: 0.40,
  },
  {
    feature: 'whatsAppAudio',
    triggerPercentage: 0, // AGC: Show when trying to use (free has 0)
    message: 'Integração WhatsApp não disponível no plano gratuito',
    upgradeMessage: 'Envie áudios pelo WhatsApp com o plano PRO',
    conversionRate: 0.40,
  },
  {
    feature: 'sentinelImages',
    triggerPercentage: 100,
    message: 'Você usou suas {current} imagens de satélite Sentinel-2 este mês',
    upgradeMessage: 'Faça upgrade para PRO e tenha 5 imagens/mês + PlanetScope',
    conversionRate: 0.45,
  },
  {
    feature: 'planetScopeImages',
    triggerPercentage: 0, // AGC: Show when trying to use (free has no access)
    message: 'Imagens PlanetScope (3m) não disponíveis no plano gratuito',
    upgradeMessage: 'Faça upgrade para PRO e acesse imagens de alta resolução',
    conversionRate: 0.50,
  },
  {
    feature: 'airbusImages',
    triggerPercentage: 0, // AGC: Show when trying to use (free has no access)
    message: 'Imagens Airbus Pleiades (50cm) disponíveis apenas para PRO/Premium',
    upgradeMessage: 'Faça upgrade para PRO e acesse imagens de altíssima resolução',
    conversionRate: 0.35,
  },
]

/**
 * AGC: Get the required tier for a specific feature
 */
export function getRequiredTierForFeature(feature: keyof TierLimits): SubscriptionTier {
  // AGC: Check if free tier has this feature
  if (typeof TIER_LIMITS.free[feature] === 'boolean') {
    if (TIER_LIMITS.free[feature]) return 'free'
    if (TIER_LIMITS.pro[feature]) return 'pro'
    return 'premium'
  }
  
  // AGC: For numeric limits, free is always available (may be 0)
  return 'free'
}

/**
 * AGC: Check if a tier has access to a boolean feature
 */
export function tierHasFeature(tier: SubscriptionTier, feature: keyof TierLimits): boolean {
  const value = TIER_LIMITS[tier][feature]
  if (typeof value === 'boolean') return value
  if (typeof value === 'number') return value > 0
  return true // AGC: null means unlimited
}

/**
 * AGC: Get next tier for upgrade
 */
export function getNextTier(currentTier: SubscriptionTier): SubscriptionTier | null {
  if (currentTier === 'free') return 'pro'
  if (currentTier === 'pro') return 'premium'
  return null // AGC: Already at highest tier
}

/**
 * AGC: Get previous tier for downgrade
 */
export function getPreviousTier(currentTier: SubscriptionTier): SubscriptionTier | null {
  if (currentTier === 'premium') return 'pro'
  if (currentTier === 'pro') return 'free'
  return null // AGC: Already at lowest tier
}

/**
 * AGC: Calculate total price including hectare add-ons
 */
export function calculateTotalPrice(
  tier: SubscriptionTier,
  billingCycle: BillingCycle,
  totalHectares: number
): { basePrice: number; addonPrice: number; total: number } {
  const pricing = TIER_PRICING[tier]
  const limits = TIER_LIMITS[tier]
  const basePrice = billingCycle === 'annual' ? pricing.annual : pricing.monthly
  
  // AGC: Calculate hectare add-on if exceeding limit
  let addonPrice = 0
  const maxHectares = limits.maxHectares
  
  if (maxHectares !== null && totalHectares > maxHectares) {
    const excessHectares = totalHectares - maxHectares
    
    for (const addon of HECTARE_ADDON_PRICING) {
      if (addon.rangeStart > totalHectares) break
      
      const rangeEnd = addon.rangeEnd ?? totalHectares
      const hectaresInRange = Math.min(rangeEnd, totalHectares) - Math.max(addon.rangeStart, maxHectares)
      
      if (hectaresInRange > 0) {
        const rate = tier === 'premium' ? addon.premiumRate : addon.proRate
        addonPrice += hectaresInRange * rate
      }
    }
    
    // AGC: For annual billing, multiply addon by 12
    if (billingCycle === 'annual') {
      addonPrice *= 12
    }
  }
  
  return {
    basePrice,
    addonPrice,
    total: basePrice + addonPrice,
  }
}

export default {
  TIER_LIMITS,
  TIER_PRICING,
  TIER_DISPLAY_INFO,
  FEATURE_COMPARISON,
  UPGRADE_TRIGGERS,
  DEFAULT_SUBSCRIPTION,
  DEFAULT_USAGE,
  DEFAULT_TIER_CONFIGS,
}





